import {
  put,
  takeLatest,
  select,
  delay,
  type SagaReturnType,
} from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import { isEmpty, isArray, pickBy } from 'lodash';

import { getIsHotelOnly } from 'src/store/view/selectors';
import { clearTouristData } from 'src/store/tourists/slice';
import { logger } from 'src/error';
import { api } from '../../../../api';

import {
  SIMILAR_PACKAGES_STATE,
  SIMILAR_PACKAGES_SEARCH_DELAY,
} from '../constants/similar-packages';

import {
  similarPackagesPopupOpenedAction,
  similarPackagesFail,
  similarPackagesFinishAction,
  createPackageAction,
  similarPackagesPopupClosedAction,
  similarPackagesCreatedRequestAction,
  clearSearchAction,
  similarPackagesStartAction,
} from '../actions/similar-packages';

import { startActualizationProcessAction } from '../actions/actualization';

import { setAnalytics } from '../actions/analytics';
import { prebuildOrderAction } from '../actions/order';
import {
  clearPreviousPackageDataAction,
  createPackageSuccess,
  updatePackageId,
} from '../actions/package';

import { getPath } from '../selectors/router';
import { getIsFirstStepDone } from '../selectors/order';
import {
  getTourId,
  getHotelId,
  getPackageId,
  getOperatorId,
  getDepartureCityId,
  getStartDate,
  makeGetKids,
  getMealCode,
  getAdultsCount,
  getNightsInHotel,
  getArchivedAt,
} from '../selectors/package';
import {
  getSimilarPackageIsPopupOpened,
  getSimilarPackagesRequestId,
  getIsExcludeOperator,
  getSimilarPackagesAdjustParams,
  getSimilarPackagesHasAdjustParams,
  getSimilarPackagesHasChangedTourists,
} from '../selectors/similar-packages';
import { goToHotelSectionAction } from '../actions/view';
import { getRoomInfoNameRu } from '../domains/room-info/selectors';

/**
 * Обработка ошибки
 * @param {*} errorMessage
 * @param {*} error
 */
function* handleSimilarPackagesFail(errorMessage, error) {
  yield put(similarPackagesFail(error));
  logger(error, errorMessage);
}

/**
 * Собирает параметры для запроса
 */
function* getRequestParams() {
  const hasAdjust: SagaReturnType<typeof getSimilarPackagesHasAdjustParams> =
    yield select(getSimilarPackagesHasAdjustParams);
  const adjust: SagaReturnType<typeof getSimilarPackagesAdjustParams> =
    yield select(getSimilarPackagesAdjustParams);
  const adjustKids =
    isArray(adjust.kidsAges) && isEmpty(adjust.kidsAges)
      ? ['']
      : adjust.kidsAges;

  // TODO: собрать параметры в одном селекторе, как getSimilarPackagesAdjustParams
  const isExcludeOperator: SagaReturnType<typeof getIsExcludeOperator> =
    yield select(getIsExcludeOperator);
  const operatorId: SagaReturnType<typeof getOperatorId> = yield select(
    getOperatorId,
  );
  const excludeOperator = isExcludeOperator ? [operatorId] : null;

  const _tourId: SagaReturnType<typeof getTourId> = yield select(getTourId);
  const tourId = !hasAdjust ? _tourId : null;
  const startDate: SagaReturnType<typeof getStartDate> = yield select(
    getStartDate,
  );
  const departureId: SagaReturnType<typeof getDepartureCityId> = yield select(
    getDepartureCityId,
  );
  const nightsInHotel: SagaReturnType<typeof getNightsInHotel> = yield select(
    getNightsInHotel,
  );
  const adultsCount: SagaReturnType<typeof getAdultsCount> = yield select(
    getAdultsCount,
  );
  const kidsAges: SagaReturnType<typeof makeGetKids> = yield select(
    makeGetKids,
  );
  const hotelId: SagaReturnType<typeof getHotelId> = yield select(getHotelId);
  const pansion: SagaReturnType<typeof getMealCode> = yield select(getMealCode);
  const roomNameRu: SagaReturnType<typeof getRoomInfoNameRu> = yield select(
    getRoomInfoNameRu,
  );

  const searchParams = {
    start_date: startDate,
    departure_id: departureId,
    nights_count: nightsInHotel,
    adults_count: adultsCount,
    kids_ages: kidsAges,
    hotel_id: hotelId,
    operator_id: operatorId,
    room_type: roomNameRu,
    pansion_name: pansion,
  };

  const adjustParams = {
    start_date: adjust.date,
    nights_count: adjust.nights,
    adults_count: adjust.adults,
    kids_ages: adjustKids,
  };

  // exclude null value from obj
  const params = pickBy({
    exclude_operators_ids: excludeOperator,
    tour_id: tourId,
    search_params: searchParams,
    adjust_params: adjustParams,
  });

  return params;
}

/**
 * Сохраняет данные в стор
 * @param {Array} response
 */
// eslint-disable-next-line consistent-return
function* saveData(response) {
  const { state } = response;

  yield put(similarPackagesFinishAction(response));

  if (state === SIMILAR_PACKAGES_STATE.EQUIVALENT) {
    const { id, request_id: requestId } = response.tours[0];

    const paramsForCreatePackage = {
      tour_id: id,
      request_id: requestId,
    };

    return yield put(createPackageAction(paramsForCreatePackage));
  }
  return null;
}

/**
 * Получает похожие туры
 */
function* fetch() {
  try {
    const requestId: SagaReturnType<typeof getSimilarPackagesRequestId> =
      yield select(getSimilarPackagesRequestId);

    if (!requestId) throw new Error('Не известен request_id');

    while (true) {
      yield delay(SIMILAR_PACKAGES_SEARCH_DELAY);

      const response = yield api.getSimilarToursShow({ request_id: requestId });

      if (response.state !== SIMILAR_PACKAGES_STATE.PERFORMING) {
        yield saveData(response);
        break;
      }
    }
  } catch (error) {
    yield handleSimilarPackagesFail(
      'Невозможно получить похожие пакеты',
      error,
    );
  }
}

/**
 * Создает запрос на похожие туры
 */
function* createRequest() {
  try {
    const params = yield getRequestParams();
    const hasAdjust: SagaReturnType<typeof getSimilarPackagesHasAdjustParams> =
      yield select(getSimilarPackagesHasAdjustParams);

    if (!hasAdjust) {
      yield put(similarPackagesPopupOpenedAction());
    }

    // TODO: Когда симилар начнет работать с отельным поиском выпилить это
    const isHotelOnly: SagaReturnType<typeof getIsHotelOnly> = yield select(
      getIsHotelOnly,
    );
    if (isHotelOnly) {
      yield put(similarPackagesFail(undefined));
      return;
    }

    const response = yield api.postSimilarToursCreate(params);

    yield put(similarPackagesCreatedRequestAction(response.request_id));
  } catch (error) {
    yield handleSimilarPackagesFail(
      'Невозможно создать запрос на похожие пакеты',
      error,
    );
  }
}

/**
 * Создание пакета
 */
function* handleCreatePackage(action: ReturnType<typeof createPackageAction>) {
  const { tour_id, request_id } = action.payload;

  const hasAdjust: SagaReturnType<typeof getSimilarPackagesHasAdjustParams> =
    yield select(getSimilarPackagesHasAdjustParams);
  const hasChangedTourists: SagaReturnType<
    typeof getSimilarPackagesHasChangedTourists
  > = yield select(getSimilarPackagesHasChangedTourists);
  const packageId: SagaReturnType<typeof getPackageId> = yield select(
    getPackageId,
  );

  const fromPackage = hasAdjust ? undefined : packageId;

  try {
    const response = yield api.getSearchGetOffer({
      tour_id,
      request_id,
      from_package: fromPackage,
    });

    if (!response.success) {
      throw new Error('cant create new package');
    }

    // если поменялись параметры тура, удалить ранее выбранный перелет и шаг топбара
    if (hasAdjust) {
      yield put(clearPreviousPackageDataAction());
    }

    // если в пакете поменялся состав туристов, удалить данные о туристах
    if (hasChangedTourists) {
      yield put(clearTouristData());
    }

    yield put(createPackageSuccess(response.package));
    yield put(similarPackagesPopupClosedAction());
  } catch (error) {
    yield handleSimilarPackagesFail('Невозможно создать новый пакет', error);
  }
}

/**
 * Меняет урл при смене packageId
 * NOTE: При смене урла срабатывает событие ccx-event – оно срабатывает на History.pushState
 */
function* handleChangePackageId(action) {
  const packageId: SagaReturnType<typeof getPackageId> = yield select(
    getPackageId,
  );
  const path = yield select(getPath);
  const newPath = path.replace(/[0-9]+/, packageId);
  yield put(setAnalytics());
  yield put(replace(newPath));

  // не обновляем при улучшении номера
  if (action?.type !== updatePackageId.type) {
    // Актуализация после симилара должна быть без flight_pair_id
    yield put(clearPreviousPackageDataAction());
    yield put(goToHotelSectionAction());
  }

  const isArchived: SagaReturnType<typeof getArchivedAt> = yield select(
    getArchivedAt,
  );
  if (!isArchived) {
    yield put(startActualizationProcessAction());
  }

  const isFirstStepDone: SagaReturnType<typeof getIsFirstStepDone> =
    yield select(getIsFirstStepDone);

  if (isFirstStepDone) {
    yield put(prebuildOrderAction());
  }
}

function* handleClearSearcherParams() {
  yield put(clearSearchAction());
}

function* handleOpenPopup() {
  const hasAdjust: SagaReturnType<typeof getSimilarPackagesHasAdjustParams> =
    yield select(getSimilarPackagesHasAdjustParams);
  const isPopupOpened: SagaReturnType<typeof getSimilarPackageIsPopupOpened> =
    yield select(getSimilarPackageIsPopupOpened);

  if (hasAdjust && !isPopupOpened) {
    yield put(similarPackagesPopupOpenedAction());
  }
}

export default function* similarPackagesSaga() {
  yield takeLatest(similarPackagesStartAction, createRequest);
  yield takeLatest(similarPackagesCreatedRequestAction, fetch);
  yield takeLatest(similarPackagesPopupClosedAction, handleClearSearcherParams);
  yield takeLatest(
    [similarPackagesFinishAction, similarPackagesFail],
    handleOpenPopup,
  );
  // CREATE
  yield takeLatest(createPackageAction, handleCreatePackage);
  yield takeLatest(
    [createPackageSuccess, updatePackageId],
    handleChangePackageId,
  );
}
